import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plane } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);

  const go = (user) => navigate(user.role === "admin" ? "/admin" : "/client");

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(loginForm.email, loginForm.password);
      toast.success("Connexion réussie");
      go(user);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await register(regForm);
      toast.success("Compte créé avec succès");
      go(user);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-12">
      <div className="text-center mb-8">
        <span className="h-14 w-14 rounded-2xl bg-primary flex items-center justify-center mx-auto mb-4">
          <Plane className="h-7 w-7 text-accent" />
        </span>
        <h1 className="font-head text-3xl font-extrabold text-primary">Bienvenue</h1>
        <p className="text-muted-foreground">Connectez-vous ou créez votre compte</p>
      </div>

      <div className="bg-white rounded-2xl soft-shadow border border-gray-100 p-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 w-full rounded-full mb-6">
            <TabsTrigger value="login" className="rounded-full" data-testid="tab-login">Connexion</TabsTrigger>
            <TabsTrigger value="register" className="rounded-full" data-testid="tab-register">Inscription</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <form onSubmit={doLogin} className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-primary">E-mail</label>
                <Input required type="email" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} className="rounded-xl" data-testid="login-email" />
              </div>
              <div>
                <label className="text-sm font-semibold text-primary">Mot de passe</label>
                <Input required type="password" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} className="rounded-xl" data-testid="login-password" />
              </div>
              <Button type="submit" disabled={loading} className="w-full h-11 rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold" data-testid="login-submit">
                {loading ? "Connexion..." : "Se connecter"}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="register">
            <form onSubmit={doRegister} className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-primary">Nom complet</label>
                <Input required value={regForm.name} onChange={(e) => setRegForm({ ...regForm, name: e.target.value })} className="rounded-xl" data-testid="register-name" />
              </div>
              <div>
                <label className="text-sm font-semibold text-primary">E-mail</label>
                <Input required type="email" value={regForm.email} onChange={(e) => setRegForm({ ...regForm, email: e.target.value })} className="rounded-xl" data-testid="register-email" />
              </div>
              <div>
                <label className="text-sm font-semibold text-primary">Téléphone</label>
                <Input value={regForm.phone} onChange={(e) => setRegForm({ ...regForm, phone: e.target.value })} className="rounded-xl" data-testid="register-phone" />
              </div>
              <div>
                <label className="text-sm font-semibold text-primary">Mot de passe (min. 6 caractères)</label>
                <Input required type="password" value={regForm.password} onChange={(e) => setRegForm({ ...regForm, password: e.target.value })} className="rounded-xl" data-testid="register-password" />
              </div>
              <Button type="submit" disabled={loading} className="w-full h-11 rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold" data-testid="register-submit">
                {loading ? "Création..." : "Créer mon compte"}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
